import { useMemo, useState } from 'react';
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DeviceTypeNames } from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_FIELDS_METADATA,
  getDeviceTypeIdByName,
} from '@/features/devices/schemas/devices.metadata.ts';
import { Input } from '@/components/ui/input.tsx';
import { LoaderIcon, SquarePen } from 'lucide-react';
import { useUpdateDeviceOverviewConfig } from '@/features/devices/hooks/mutations/[deviceId]/use-update-device-overview-config';
import { TDeviceOverviewConfig } from '@/features/devices/hooks/queries/[device]/get-device-overview.ts';

type DeviceField = {
  id: number;
  name: string;
  unit: string;
  decimals: number;
};

type DeviceFieldsPickerDialogProps = {
  deviceId: string;
  deviceType: DeviceTypeNames;
  plantId?: string;
  config?: TDeviceOverviewConfig;
};

export const DeviceFieldsPickerDialog = ({
  deviceId,
  deviceType,
  plantId,
  config,
}: DeviceFieldsPickerDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <Credenza open={open} onOpenChange={setOpen}>
      <CredenzaTrigger asChild>
        <Button variant="outline">
          <SquarePen className="size-5" />
        </Button>
      </CredenzaTrigger>
      {open && (
        <DeviceFieldsPickerDialogContent
          deviceId={deviceId}
          deviceType={deviceType}
          plantId={plantId}
          config={config}
          onClose={() => setOpen(false)}
        />
      )}
    </Credenza>
  );
};

type DeviceFieldsPickerDialogContentProps = DeviceFieldsPickerDialogProps & {
  onClose: () => void;
};

function DeviceFieldsPickerDialogContent({
  deviceId,
  deviceType,
  plantId,
  config,
  onClose,
}: DeviceFieldsPickerDialogContentProps) {
  const deviceTypeCode = getDeviceTypeIdByName(deviceType);

  const fields = useMemo<DeviceField[]>(() => {
    return Object.values(
      DEVICE_FIELDS_METADATA[deviceTypeCode].parametersByCode
    ) as DeviceField[];
  }, [deviceTypeCode]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(
    () => new Set(config?.fields ?? [])
  );

  const filteredFields = useMemo(() => {
    if (!searchQuery) return fields;
    const query = searchQuery.toLowerCase();
    return fields.filter((field) => field.name.toLowerCase().includes(query));
  }, [fields, searchQuery]);

  const handleToggleField = (fieldId: number) => {
    setSelectedFieldIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedFieldIds(new Set(fields.map((f) => f.id)));
  };

  const handleDeselectAll = () => {
    setSelectedFieldIds(new Set());
  };

  const { mutate, isPending } = useUpdateDeviceOverviewConfig({
    onSuccess: onClose,
  });

  const handleSave = () => {
    const fields = Array.from(selectedFieldIds);

    mutate({
      deviceId,
      plantId,
      config: { fields },
    });
  };

  return (
    <CredenzaContent className="max-w-2xl">
      <CredenzaHeader>
        <CredenzaTitle>Select Fields for {deviceType}</CredenzaTitle>
        <CredenzaDescription>Choose which device fields to display.</CredenzaDescription>
      </CredenzaHeader>

      <CredenzaBody className="px-1 sm:px-0">
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-3 sm:px-0">
            <Input
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSelectAll}
              className="h-9 text-xs"
            >
              All
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDeselectAll}
              className="h-9 text-xs"
            >
              None
            </Button>
          </div>

          <div className="text-muted-foreground px-3 text-xs sm:px-0">
            {selectedFieldIds.size}/{fields.length} selected
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-2 px-3 sm:px-0">
              {filteredFields.map((field) => (
                <div
                  key={field.id}
                  className="hover:bg-accent flex items-start space-x-3 rounded-md p-3 transition-colors"
                >
                  <Checkbox
                    id={`field-${field.id}`}
                    checked={selectedFieldIds.has(field.id)}
                    onCheckedChange={() => handleToggleField(field.id)}
                    className="mt-0.5 cursor-pointer"
                  />
                  <Label
                    htmlFor={`field-${field.id}`}
                    className="group flex-1 cursor-pointer text-sm leading-none font-medium"
                  >
                    <div className="flex flex-col gap-1 group-not-has-[p]:mt-0.5">
                      <span title={String(field.id)}>{field.name}</span>
                      {field.unit && (
                        <p className="text-muted-foreground text-xs">Unit: {field.unit}</p>
                      )}
                    </div>
                  </Label>
                </div>
              ))}

              {filteredFields.length === 0 && (
                <div className="text-muted-foreground py-8 text-center">
                  No fields found matching "{searchQuery}"
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </CredenzaBody>

      <CredenzaFooter>
        <CredenzaClose asChild>
          <Button variant="outline">Cancel</Button>
        </CredenzaClose>
        <Button disabled={isPending} onClick={handleSave}>
          {isPending && (
            <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
          )}
          Save Changes
        </Button>
      </CredenzaFooter>
    </CredenzaContent>
  );
}
